import { useCallback } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  Form,
  Input,
  Spinner,
  SubmitButton,
  Text,
  useTextField,
  ValidateFunction,
  VStack,
} from 'components/uikit';
import { paymentFormAtom, setPaymentNameAtom } from 'data/payments/atoms';
import { isFormLoading, isFormSuccess } from 'utils/atoms/dataFetchingAtom';

const validateName: ValidateFunction<string> = (name) => {
  if (!name)
    return {
      inputStatus: 'ERROR',
      message: 'name_input_error_empty',
    };
  const [firstName, lastName] = name.split(' ');

  if (!firstName || !lastName) {
    return {
      inputStatus: 'ERROR',
      message: 'name_input_error_single',
    };
  }

  if (firstName.length < 2 || lastName.length < 2) {
    return {
      inputStatus: 'ERROR',
      message: 'name_input_error_short',
    };
  }
  return {
    inputStatus: 'SUCCESS',
  };
};

interface PaymentNameFormDefaultProps {
  depositId: string;
  amount: string;
}

export function PaymentNameFormDefault({
  depositId,
  amount,
}: PaymentNameFormDefaultProps) {
  const { t } = useTranslation('payments');

  const { status, errorMessage } = useAtomValue(paymentFormAtom);
  const updateName = useSetAtom(setPaymentNameAtom);

  const nameInput = useTextField({
    name: 'name',
    t,
    validate: validateName,
  });
  const name = nameInput.value;
  const isValid = nameInput.isValid;
  const isLoading = isFormLoading({ status });
  const isSuccess = isFormSuccess({ status });

  const onSubmit = useCallback(() => {
    if (name && isValid) {
      updateName({
        depositId,
        payerName: name,
      });
    }
  }, [name, isValid, depositId, updateName]);

  return (
    <Form onSubmit={onSubmit}>
      <VStack spacing='32px'>
        <VStack spacing='0' align='start' alignSelf='start'>
          <Text
            as='h3'
            fontSize='lg'
            fontWeight='medium'
            color='grey.tertiaryText'
          >
            {t('payment_amount')}
          </Text>
          <Text as='h3' fontSize='3xl' fontWeight='semibold' variant='numeric'>
            {amount} RUB
          </Text>
        </VStack>
        <VStack spacing='12px'>
          <Text
            as='h3'
            fontSize='lg'
            fontWeight='medium'
            color='grey.tertiaryText'
          >
            {t('name_input_label')}
          </Text>
          <Input
            w='360px'
            placeholder={t('name_input_placeholder')}
            {...nameInput}
          />
        </VStack>
        <SubmitButton
          w='360px'
          size='lg'
          variant='primary'
          disabled={!isValid || isLoading || isSuccess}
          rightIcon={isLoading || isSuccess ? <Spinner /> : undefined}
        >
          {t('name_button')}
        </SubmitButton>
        {errorMessage ? (
          <Text color='error.text' fontSize='sm'>
            {errorMessage}
          </Text>
        ) : null}
      </VStack>
    </Form>
  );
}
